import { Badge, Box, Card, CircularProgress, Icon, FormHelperText, Grid, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import defaultImage from 'assets/images/placeholder-image.png';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { validateMainStrain } from 'validations/strain';
import { openSnackbar, uploadImage } from '../../redux/slices/common';
import { isEmptyObject } from 'utilities/common';
import { getMainStrain, updateMainStrain } from '../../redux/slices/strain';
import CommonQuillEditor from '../../common/editor';
import { useNavigate } from 'react-router-dom';
import colors from 'assets/theme/base/colors';
import StiloForm from './stiloForm';
import { STILO } from 'utilities/constants';
import { getData } from 'utilities/common';
import DeleteIcon from "common/customIcons/delete";
import SmartToyIcon from '@mui/icons-material/SmartToy'; // AI robot icon
import { convertAndExtractStrainMeta } from 'utilities/common';
import { mainStrainOverviewPagePrompt } from 'prompt';

export default function AddMainStrainForm() {
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const { mainStrainData, loading = false } = useSelector(state => state.strain);
  const { isUploading } = useSelector(state => state.common);
  const { grey } = colors;
  const adminsMode = getData('adminsMode')
  const isStilo = JSON.parse(adminsMode) === STILO
  const [faqError, setFaqError] = useState("");

  const [strainData, setStrainData] = useState({
    image: {},
    metaTitle: '',
    metaDescription: '',
    title: '',
    description: '',
    faq: []
  });
  const navigate = useNavigate();
  const [isPromptLoading, setPromptIsLoading] = useState(false)

  const strainImageUploader = async (e) => {
    if (e.target.files.length) {
      const file = e.target.files[0];
      const body = new FormData();
      body.append('filePath', file);
      // res.fileUrl
      dispatch(uploadImage(body)).unwrap().then((res => {
        setStrainData({ ...strainData, image: { url: res.fileUrl } });
      }));
    }
  };

  const showError = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const addMainStrain = () => {
    const { errors: validationErrors, isValid: isDataValid } = validateMainStrain(strainData);
    if (!isDataValid) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    dispatch(updateMainStrain(strainData)).unwrap().then((res) => {
      showError(res?.message, 'success');
      navigate('/strains');
    });

  };
  useEffect(() => {
    if (!isEmptyObject(mainStrainData)) {
      setStrainData({ ...strainData, image: mainStrainData?.image, title: mainStrainData?.title, description: mainStrainData?.description, metaDescription: mainStrainData?.metaDescription, metaTitle: mainStrainData?.metaTitle, faq: mainStrainData?.faq || [] });
    }
  }, [mainStrainData]);

  useEffect(() => {
    dispatch(getMainStrain());
  }, []);

  const uploadThumbnailImage = {
    borderRadius: '20px',
    width: '160px',
    height: '160px',
    objectFit: 'cover',
    boxShadow: '0 0 1px rgb(0 0 0 / 20%)'
  };
  const badgeCircularProgressIcon = {
    backgroundColor: '#fff',
    borderRadius: 10,
    p: .5,
    mr: 14,
    mt: 14
  };

  const handleRemoveFAQ = (faQIdx) => {
    let FAQArray = [...(strainData?.faq || [])];
    FAQArray = FAQArray.filter((faqs, _idx) => _idx !== faQIdx);
    setStrainData({
      ...strainData,
      faq: FAQArray,
    });
  };

  const isLastFAQFilled = () => {
    const faqs = strainData.faq || [];
    if (faqs.length === 0) return true;

    const last = faqs[faqs.length - 1];
    return Boolean(
      (last?.question && last.question.trim() !== "") || (last?.answer && last.answer.trim() !== "")
    );
  };
  const addFAQHandler = () => {
    if (!isLastFAQFilled()) {
      setFaqError("Please fill the previous FAQ before adding a new one.");
      return;
    }
    setFaqError("");
    let newFAQ = [...(strainData?.faq || [])];
    let faqItem = {
      // question: "",
      // answer: "",
    };
    newFAQ.push(faqItem);
    setStrainData({
      ...strainData,
      faq: newFAQ,
    });
  };
  const handleChangeFAQ = (value, fieldType, idx) => {
    let FAQArray = [...(strainData?.faq || [])];
    if (FAQArray[idx]) {
      FAQArray[idx] = {
        ...FAQArray[idx],
        [fieldType]: value,
      };
      setStrainData({
        ...strainData,
        faq: FAQArray,
      });
    }
  };

  const handleGenerate = useCallback(async () => {
    setPromptIsLoading(true);
    setStrainData(prev => ({
      ...prev,
      description: '',
      metaTitle: '',
      metaDescription: '',
      faq: [],
      title: ""
    }));

    try {
      const prompt = mainStrainOverviewPagePrompt({ platform: "hemp" });
      const payload = { prompt: prompt };
      const res = await fetch(`${process.env.REACT_APP_STILO_API_BASE_URL}/api/v1/generate/description`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_AUTH_KEY
        },
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let fullContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        fullContent += chunk;

        // Optional: real-time update

        const finalResult = convertAndExtractStrainMeta(fullContent);

        setStrainData((prev) => ({
          ...prev,
          description: finalResult.description || finalResult.longDescription,
          metaTitle: finalResult.metaTitle || prev.metaTitle,
          metaDescription: finalResult.metaDescription,
          title: finalResult.title || prev.title,
          faq: finalResult.faq || [],
          image: {
            url: prev?.image?.url || '',
            alt: finalResult?.altImage
          },
        }));
      }


    } catch (error) {
    } finally {
      setPromptIsLoading(false);
    }
  }, []);
  const onChangehandler = (e) => {
    const { name, value } = e.target;
    setStrainData({ ...strainData, [name]: value });
  };

  return (
    <Box p={{ xs: 2, sm: 3, lg: 4 }}>
      <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: 'none' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant='h4' fontWeight="semibold" color="secondary" mb={4.5}>Edit Main Strain</Typography>
          {
            !isStilo &&    <Box display={'flex'} alignItems="center" gap={1}>
            <Typography variant="body2" fontWeight="medium" color="secondary">
              Generate Meta Content
            </Typography>

            <MDButton
              variant="contained"
              color="primary"
              size="small"
              onClick={handleGenerate}
              disabled={isPromptLoading}
              startIcon={!isPromptLoading && <SmartToyIcon />} // Show icon only when not loading
            >
              {isPromptLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Generate'
              )}
            </MDButton>
          </Box>
          }
      
        </Box>
        {/* <Typography variant='body2' fontWeight='medium' mb={1} color="#2E2E3A">Meta Properties</Typography> */}

        {isStilo ?
          <Grid container spacing={3.5}>
            <StiloForm
              errors={errors}
              strainData={strainData}
              setStrainData={setStrainData}
            />
          </Grid>
          :
          <>
            <Typography variant='h5' fontWeight="semibold" mb={2}>{isStilo ? "Banner Image" : "Meta Image"}</Typography>

            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25} mb={3}>

              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right', }}
                badgeContent={
                  isUploading ? <CircularProgress size={32} color='primary' sx={badgeCircularProgressIcon} /> : ''
                }
              >
                <img style={uploadThumbnailImage} src={strainData?.image?.url || defaultImage} alt="" />
              </Badge>

              <label htmlFor="image-input">
                <MDButton
                  sx={{ minWidth: '180px' }}
                  type='button'
                  variant='outlined'
                  color='secondary'
                  component='span'
                  disabled={isUploading}
                >
                  Change Image
                </MDButton>
              </label>
              <MDInput
                id="image-input"
                type="file"
                name="image"
                onChange={strainImageUploader}
                fullWidth
                disabled={isUploading}
                style={{ display: 'none' }}
              />
              {Boolean(errors?.image) && <FormHelperText sx={{ width: '100%', ml: 0, mt: -.5 }} error>{errors?.image}</FormHelperText>}

            </Stack>
            <Grid item xs={12} sm={6} mb={3} style={{ maxWidth: '20%' }}>
              <TextField
                label="Alt Text"
                value={strainData?.image?.alt || ""}
                onChange={(e) => {
                  setStrainData({ ...strainData, image: { ...strainData.image, alt: e.target.value } });
                }}
                variant="outlined"
                fullWidth
              />
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>Title</Typography>
                <div className="remove-quill-mb">
                  <CommonQuillEditor
                    value={strainData?.title || ''}
                    onChange={(value) => setStrainData({ ...strainData, title: value })}
                  />
                </div>
              </Grid>
              <Grid item xs={12} className="txt-left">
                <TextField
                  name="metaTitle"
                  value={strainData.metaTitle || ''}
                  onChange={onChangehandler}
                  label="Meta Title"
                  className="w-100"
                  margin="normal"
                  error={errors.metaTitle}
                  helperText={errors?.metaTitle}
                />
              </Grid>
              <Grid item xs={12} className="txt-left" >
                <TextField
                  name="metaDescription"
                  value={strainData.metaDescription || ''}
                  onChange={onChangehandler}
                  label="Meta Description"
                  className="w-100"
                  margin="normal"
                  error={errors.metaDescription}
                  helperText={errors?.metaDescription}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>Description</Typography>
                <div className="remove-quill-mb">
                  <CommonQuillEditor
                    value={strainData?.description || ''}
                    onChange={(value) => setStrainData({ ...strainData, description: value })}
                  />
                </div>
              </Grid>
            </Grid>
          </>
        }
        {
          !isStilo &&
          <>
            <Typography variant="h5" fontWeight="semibold" mb={2} mt={4}>
              FAQs
            </Typography>
            {strainData?.faq?.length > 0 ? (
              <>
                {strainData?.faq?.map((faqs, idx) => {
                  return (
                    <Grid key={idx} item xs={12} sm={12} lg={12} my={2}>
                      <Box width={"100%"} className="faq-sec-rep">
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="space-between"
                          alignItems={"flex-start"}
                          className="mb-3"
                        >
                          <Box
                            width={"100%"}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Typography variant="subtitle2" fontWeight="medium" color={grey[600]}>
                              Question
                            </Typography>
                            <MDButton
                              sx={{ gap: 0.5 }}
                              size="small"
                              variant="text"
                              onClick={() => handleRemoveFAQ(idx)}
                              style={{ color: "#fd4438" }}
                            >
                              <DeleteIcon width="1rem" height="1rem" style={{ color: "#fd4438" }} />{" "}
                              Delete
                            </MDButton>
                          </Box>
                          <Box width={"100%"}>
                            <CommonQuillEditor
                              allowHeading={false}
                              allowImage={false}
                              value={faqs?.question || ""}
                              onChange={(value) => {
                                handleChangeFAQ(value, "question", idx);
                                setFaqError("");
                              }}
                            />
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent="center" alignItems={"flex-start"}>
                          <Box width={"100%"}>
                            <Typography
                              variant="subtitle2"
                              fontWeight="medium"
                              color={grey[600]}
                              mb={1}
                            >
                              Answer
                            </Typography>
                            <CommonQuillEditor
                              allowHeading={false}
                              allowImage={false}
                              value={faqs?.answer || ""}
                              onChange={(value) => {
                                handleChangeFAQ(value, "answer", idx);
                                setFaqError("");
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </>
            ) : (
              <Grid item xs={12} sm={12} lg={12} mt={3}>
                <Box display="flex" justifyContent="space-between" alignItems={"center"}>
                  <MDButton
                    type="button"
                    color="primary"
                    size="medium"
                    variant="outlined"
                    sx={{ minWidth: "180px" }}
                    onClick={addFAQHandler}
                  >
                    <Icon>add</Icon>&nbsp;Add FAQ
                  </MDButton>
                </Box>
              </Grid>
            )}
            {faqError && (
              <Typography color="error" variant="body2" mb={1}>
                {faqError}
              </Typography>
            )}
          </>
        }


        {strainData?.faq?.length > 0 && (
          <Grid item xs={12} mt={3}>
            <MDButton
              type="button"
              color="primary"
              size="medium"
              variant="outlined"
              sx={{ minWidth: "180px" }}
              onClick={addFAQHandler}
            >
              <Icon>add</Icon>&nbsp;Add More
            </MDButton>
          </Grid>
        )}


        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={4} mb={2}>
          <MDButton
            sx={{ minWidth: '160px' }}
            type='submit'
            variant='contained'
            color='primary'
            onClick={addMainStrain}
            loading={loading}
          >
            {loading ? <CircularProgress size={22} style={{ color: 'white' }} /> : 'Update'}
          </MDButton>
          <MDButton
            sx={{ minWidth: '160px' }}
            type='button'
            variant='outlined'
            color='dark'
            onClick={() => navigate('/strains?orderFilter=main_strains')}
          >
            Cancel
          </MDButton>
        </Stack>
      </Card>
    </Box>
  );
}